import React, { type ReactNode, useMemo } from 'react';
import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';
import type {
	EnhancedUFOInteractionContextType,
	ReactProfilerTiming,
} from '@atlaskit/react-ufo/common';
import { getActiveInteraction } from '@atlaskit/react-ufo/interaction-metrics';

// These are stored outside react context to be resilient to concurrent mode
// restarting the start marker from a suspense and losing the initial render
const startTimes: Map<string, number[]> = new Map();
autoBeforeEachTest(() => {
	startTimes.clear();
});
// Keep track of the last interaction id and reset the start timers if it ever changes.
// This is to allow multi-step ssr to track the render timers from different "interaction"s seperately
let lastActiveInteraction: string | undefined;
function checkActiveInteractionAndResetStartMarksIfSet() {
	const activeInteractionId = getActiveInteraction()?.id;
	if (!!lastActiveInteraction && lastActiveInteraction !== activeInteractionId) {
		startTimes.clear();
	}
	lastActiveInteraction = activeInteractionId;
}
const onStartRender = (id: string) => {
	if (!startTimes.has(id)) {
		startTimes.set(id, []);
	}

	startTimes.get(id)?.push(performance.now());
};

const ProfilerMarker = ({ onRender }: { onRender?: () => void }) => {
	if (__SERVER__) {
		onRender?.();
	}
	return null;
};

export const SsrRenderProfilerInner = ({
	children,
	labelStack,
	onRender,
}: {
	children?: ReactNode | undefined;
	labelStack: ReactProfilerTiming['labelStack'];
	onRender: EnhancedUFOInteractionContextType['onRender'];
}): React.JSX.Element => {
	const reactProfilerId = useMemo(() => labelStack.map((l) => l.name).join('/'), [labelStack]);

	checkActiveInteractionAndResetStartMarksIfSet();
	return (
		<>
			<ProfilerMarker onRender={() => onStartRender(reactProfilerId)} />
			{children}
			<ProfilerMarker
				onRender={() => {
					const startTimesForId = startTimes.get(reactProfilerId);
					if (startTimesForId?.length) {
						const endTime = performance.now();
						const firstStartTime = startTimesForId[0];
						const lastStartTime = startTimesForId[startTimesForId.length - 1];
						const baseDuration = endTime - lastStartTime;
						const actualDuration = endTime - firstStartTime;
						onRender(
							'mount', // this is incorrect, but on the server there is no mount phase
							actualDuration,
							baseDuration,
							firstStartTime,
							endTime,
						);
					}
				}}
			/>
		</>
	);
};

const SsrRenderProfiler = (
	props: Parameters<typeof SsrRenderProfilerInner>[0],
): React.JSX.Element => {
	if (__SERVER__) {
		return <SsrRenderProfilerInner {...props} />;
	}

	// ensure structure similar to SSR implementation
	return (
		<>
			<ProfilerMarker />
			{props.children}
		</>
	);
};
export default SsrRenderProfiler;
